import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { accessOptions, visibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { copyTemplate } from 'modules/template-editor/store/templates/actions';
import { resetPressedState } from 'modules/template-editor/store/templates/templates';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';

const PRESET: TemplatePresetsNameList = 'duplicate';

function useDuplicateModal() {
  const dispatch = useDispatch();
  const dispatchWithToast = useDispatchWithToast();
  const { info } = useOrganizer();
  const { activeTemplate, templates, isProcessed, isFinished, currentProcess } = useTemplates();
  const isAdmin = useIsAdmin();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: Values) => {
      if (!activeTemplate) return;
      if (!info) return;
      const { width, height, name } = values;
      const resolution = { width: +width, height: +height };
      dispatchWithToast(
        copyTemplate({
          templateId: activeTemplate.id,
          ownerId: info.id,
          resolution,
          name: name.toString().trim(),
          process: 'duplicate template',
        }),
        { error: 'Не удалось создать копию' },
      );
    },
    [activeTemplate, dispatchWithToast, info],
  );

  const initialValue = useMemo(
    () => ({
      name: activeTemplate?.name ? activeTemplate?.name + ' (дубликат)' : '',
      width: activeTemplate?.resolution.width?.toString() ?? '',
      height: activeTemplate?.resolution.height?.toString() ?? '',
      visibility: visibilityOptions.find((option) => option.value === activeTemplate?.visibility) ?? visibilityOptions[0],
      access: accessOptions.find((option) => option.value === activeTemplate?.access) ?? accessOptions[0],
    }),
    [activeTemplate],
  );

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (currentProcess !== 'duplicate template') {
      return;
    }
    close();
    dispatch(resetPressedState());
  }, [isFinished, close, dispatch, currentProcess]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close}>
        <UDText type={'title-3'} className="mb-5">
          {templatePresets[PRESET].header}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin}
          isLoading={isProcessed}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useDuplicateModal;
